/**
 * CART FEE CALCULATOR — Checkout-time approach
 * =============================================
 * Calculates and adds fee line items when the user clicks Checkout.
 * No background syncing, no reload loops, no Measura conflicts.
 *
 * 1. PALLET FEE ($35 per pallet)
 *    - Applies to ALL products with a _pallet_qty line item property
 *    - Pallets per product = Math.ceil(quantity / pallet_qty)
 *    - Fee quantity = total pallets across all qualifying products
 *
 * 2. FLATBED DELIVERY FEE ($175 per 10 pallets)
 *    - Only products tagged "Landscape Bag" or "Concrete Hardscapes"
 *    - If _measura_amount or "Net volume" exists: spots = Math.ceil(parsed value)
 *    - Else if _pallet_qty exists: spots = Math.ceil(quantity / pallet_qty)
 *    - Fee quantity = Math.ceil(total spots / 10)
 *
 * 3. DUMP TRUCK BULK DELIVERY FEE (tiered by cubic yards, per product)
 *    - Products tagged "Bulk" — volume from _measura_amount or "Net volume" property
 *    - Each bulk product is delivered separately and gets its own fee:
 *      - ≤6 cu yd: 1 × 1-6 Cubic Yards fee ($140)
 *      - ≥7 cu yd: Math.ceil(yards / 10) × 7-10 Cubic Yards fees ($150 each)
 *    - Fees are summed across all bulk products.
 *
 * Tags are embedded as a hidden _tags property at add-to-cart time.
 * A fetch interceptor ensures Measura's add-to-cart calls also get _tags.
 */

(function () {
  "use strict";

  // ─── CONFIG ────────────────────────────────────────────────────────────────

  const PALLET_FEE_VARIANT_ID = 51453890232603;

  const DELIVERY_FEE_VARIANT_ID = 51788884803867;
  const DELIVERY_FEE_TAGS = [
    "landscape bag",
    "concrete hardscapes",
    "landscape accessories",
    "accessories",
    "artificial turf",
  ];
  const DELIVERY_PALLETS_PER_FEE = 10;

  const DUMP_TRUCK_1_6_VARIANT_ID = 51795376177435; // $140
  const DUMP_TRUCK_7_10_VARIANT_ID = 51987576946971; // $150
  const BULK_TAG = "bulk";

  const ALL_FEE_VARIANT_IDS = [
    PALLET_FEE_VARIANT_ID,
    DELIVERY_FEE_VARIANT_ID,
    DUMP_TRUCK_1_6_VARIANT_ID,
    DUMP_TRUCK_7_10_VARIANT_ID,
  ];

  // ─── CART API HELPERS ──────────────────────────────────────────────────────

  function fetchCart() {
    return fetch("/cart.js").then(function (r) {
      return r.json();
    });
  }

  function updateCartByKey(updates) {
    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (body) {
          var err = new Error(
            "[CartFees] /cart/update.js " +
              r.status +
              ": " +
              (body.description || body.message || JSON.stringify(body)),
          );
          console.error(err.message);
          throw err;
        });
      }
      return r.json();
    });
  }

  // ─── CALCULATION HELPERS ───────────────────────────────────────────────────

  function calcTotalPallets(items) {
    let total = 0;
    items.forEach(function (item) {
      if (ALL_FEE_VARIANT_IDS.indexOf(item.variant_id) !== -1) return;
      const palletQty = parseInt((item.properties || {})["_pallet_qty"], 10);
      if (!isNaN(palletQty) && palletQty > 0) {
        total += Math.ceil(item.quantity / palletQty);
      }
    });
    return total;
  }

  function calcDeliveryPallets(items) {
    let total = 0;
    items.forEach(function (item) {
      if (ALL_FEE_VARIANT_IDS.indexOf(item.variant_id) !== -1) return;

      const props = item.properties || {};
      const rawTags = (props["_tags"] || "").toLowerCase();
      const hasDeliveryTag = DELIVERY_FEE_TAGS.some(function (tag) {
        return rawTags.indexOf(tag) !== -1;
      });

      console.log(
        "[CartFees] calcDeliveryPallets item:",
        item.title,
        "| qty:", item.quantity,
        "| _tags:", props["_tags"],
        "| hasDeliveryTag:", hasDeliveryTag,
        "| _measura_amount:", props["_measura_amount"],
        "| _pallet_qty:", props["_pallet_qty"],
        "| all props:", props,
      );

      if (!hasDeliveryTag) return;

      // Measura stores total volume in _measura_amount or a visible "Net volume" property
      const measuraAmount = props["_measura_amount"] || props["Net volume"];
      if (measuraAmount) {
        const parsed = parseFloat(measuraAmount);
        if (!isNaN(parsed) && parsed > 0) {
          total += Math.ceil(parsed);
          return;
        }
      }

      // Fall back to pallet calculation (e.g. Concrete Hardscapes)
      const palletQty = parseInt(props["_pallet_qty"], 10);
      if (!isNaN(palletQty) && palletQty > 0) {
        total += Math.ceil(item.quantity / palletQty);
        return;
      }

      // No _measura_amount or _pallet_qty — each unit = 1 spot (e.g. Landscape Bags)
      total += Math.ceil(item.quantity);
    });
    console.log("[CartFees] calcDeliveryPallets total spots:", total);
    return total;
  }

  // Returns an array of cubic-yard totals, one entry per bulk product in the
  // cart. Each bulk product ships separately, so each gets its own fee.
  function calcBulkYardsByItem(items) {
    const yardsByItem = [];
    items.forEach(function (item) {
      if (ALL_FEE_VARIANT_IDS.indexOf(item.variant_id) !== -1) return;

      const props = item.properties || {};
      const rawTags = (props["_tags"] || "").toLowerCase();
      if (rawTags.indexOf(BULK_TAG) === -1) return;

      let yards = 0;
      const measuraAmount = props["_measura_amount"] || props["Net volume"];
      if (measuraAmount) {
        const parsed = parseFloat(measuraAmount);
        if (!isNaN(parsed) && parsed > 0) {
          yards = parsed;
        }
      } else {
        yards = item.quantity;
      }

      if (yards > 0) yardsByItem.push(yards);
    });
    return yardsByItem;
  }

  // ─── CHECKOUT HANDLER ──────────────────────────────────────────────────────

  /**
   * Intercept the checkout button. Calculate fees, update the cart, then
   * redirect to /checkout. Runs once per click — no loops, no reloads.
   */
  function handleCheckout() {
    var btn = document.getElementById("custom-checkout-btn");
    if (!btn || btn.disabled) return;

    // Disable the button while processing
    btn.disabled = true;
    btn.textContent = "Calculating fees…";
    btn.style.opacity = "0.6";
    btn.style.pointerEvents = "none";
    clearCheckoutError();

    console.log("[CartFees] Checkout clicked — calculating fees…");

    fetchCart()
      .then(function (cart) {
        var items = cart.items || [];

        // --- Calculate all fees ---
        var totalPallets = calcTotalPallets(items);
        var palletFeeQty = totalPallets;

        var deliveryPallets = calcDeliveryPallets(items);
        var deliveryFeeQty =
          deliveryPallets > 0
            ? Math.ceil(deliveryPallets / DELIVERY_PALLETS_PER_FEE)
            : 0;

        // Each bulk product is delivered separately, so each contributes its
        // own dump-truck fee based on its own cubic-yard total.
        var bulkYardsByItem = calcBulkYardsByItem(items);
        var dumpTruck1_6Qty = 0;
        var dumpTruck7_10Qty = 0;

        bulkYardsByItem.forEach(function (yards) {
          if (yards <= 6) {
            dumpTruck1_6Qty += 1;
          } else {
            dumpTruck7_10Qty += Math.ceil(yards / 10);
          }
        });

        console.log(
          "[CartFees] Pallets:",
          totalPallets,
          "→ pallet fee:",
          palletFeeQty,
          "| Delivery pallets:",
          deliveryPallets,
          "→ delivery fee:",
          deliveryFeeQty,
          "| Bulk yards by item:",
          bulkYardsByItem,
          "→ dump 1-6:",
          dumpTruck1_6Qty,
          "dump 7-10:",
          dumpTruck7_10Qty,
        );

        // await fetch("https://seqrgmfrqwcrrqwyqiux.supabase.co/rest/v1/website_logs", {
        //   method: "POST",
        //   headers: { 
        //     "Content-Type": "application/json",
        //     "apiKey": "sb_publishable_SBGOxeYvDYH-RtgTH57hTw_wCva_X8G" 
        //   },
        //   body: JSON.stringify({
        //     log: {
        //       event: "checkout",
        //       totalPallets: totalPallets,
        //       palletFeeQty: palletFeeQty,
        //       deliveryPallets: deliveryPallets,
        //       deliveryFeeQty: deliveryFeeQty,
        //       bulkYardsByItem: bulkYardsByItem,
        //       dumpTruck1_6Qty: dumpTruck1_6Qty,
        //       dumpTruck7_10Qty: dumpTruck7_10Qty
        //     },
        //   }),
        // });

        // try {
        //   const response = await fetch("https://seqrgmfrqwcrrqwyqiux.supabase.co/rest/v1/website_logs", {
        //     method: "POST",
        //     headers: { 
        //       "Content-Type": "application/json",
        //       "apiKey": "sb_publishable_SBGOxeYvDYH-RtgTH57hTw_wCva_X8G" 
        //     },
        //     body: JSON.stringify({
        //       log: {
        //         event: "checkout",
        //         totalPallets: totalPallets,
        //         palletFeeQty: palletFeeQty,
        //         deliveryPallets: deliveryPallets,
        //         deliveryFeeQty: deliveryFeeQty,
        //         bulkYardsByItem: bulkYardsByItem,
        //         dumpTruck1_6Qty: dumpTruck1_6Qty,
        //         dumpTruck7_10Qty: dumpTruck7_10Qty
        //       },
        //     }),
        //   });
        //   if (!response.ok) {
        //     console.warn("Log failed on server, but continuing...", response.status);
        //   }
        // } catch (error) {
        //   console.warn("Log failed to send entirely, continuing script execution.");
        // }

        // --- Single update: set all fee quantities (0 = remove) ---
        var updates = {};
        updates[PALLET_FEE_VARIANT_ID] = palletFeeQty;
        updates[DELIVERY_FEE_VARIANT_ID] = deliveryFeeQty;
        updates[DUMP_TRUCK_1_6_VARIANT_ID] = dumpTruck1_6Qty;
        updates[DUMP_TRUCK_7_10_VARIANT_ID] = dumpTruck7_10Qty;

        return updateCartByKey(updates);
      })
      .then(function () {
        console.log("[CartFees] Fees synced — redirecting in 5s (debug mode)");

        setTimeout(function () { window.location.href = "/checkout"; }, 5000);
      })
      .catch(function (err) {
        console.error("[CartFees] Error syncing fees at checkout:", err);
        // Re-enable button so user can retry — do NOT redirect without fees
        btn.disabled = false;
        btn.textContent = "Checkout";
        btn.style.opacity = "";
        btn.style.pointerEvents = "";
        showCheckoutError(
          "There was a problem preparing your order. Please try again.",
        );
      });
  }

  function showCheckoutError(msg) {
    clearCheckoutError();
    var el = document.createElement("div");
    el.id = "cart-fees-error";
    el.setAttribute("role", "alert");
    el.style.cssText =
      "color:#cc0000;font-size:14px;margin-top:10px;text-align:center;";
    el.textContent = msg;
    var btn = document.getElementById("custom-checkout-btn");
    if (btn && btn.parentNode) {
      btn.parentNode.insertBefore(el, btn.nextSibling);
    }
  }

  function clearCheckoutError() {
    var existing = document.getElementById("cart-fees-error");
    if (existing) existing.remove();
  }

  // ─── HIDE FEE CONTROLS ON CART PAGE ─────────────────────────────────────────

  /**
   * On the cart page, hide the remove/trash buttons and quantity inputs for
   * fee line items so users cannot manually delete or change them.
   * Fees are fully managed by handleCheckout — recalculated on every click.
   */
  function lockFeeLineItems() {
    // Build a set of fee variant ID strings for quick lookup
    var feeIdSet = {};
    ALL_FEE_VARIANT_IDS.forEach(function (id) {
      feeIdSet[String(id)] = true;
    });

    // Strategy 1: Find elements with data-variant-id matching a fee
    document.querySelectorAll("[data-variant-id]").forEach(function (el) {
      if (feeIdSet[el.getAttribute("data-variant-id")]) {
        hideFeeControls(el);
      }
    });

    // Strategy 2: Find cart item links whose href contains the fee variant ID
    ALL_FEE_VARIANT_IDS.forEach(function (id) {
      document
        .querySelectorAll('a[href*="' + id + '"]')
        .forEach(function (link) {
          var row = link.closest(
            "tr, li, [class*='cart-item'], [class*='cart__item'], [class*='line-item']",
          );
          if (row) hideFeeControls(row);
        });
    });

    // Strategy 3: Find remove buttons / quantity inputs whose name or value
    // references a fee variant ID (Shopify uses updates[variant_id] pattern)
    ALL_FEE_VARIANT_IDS.forEach(function (id) {
      document
        .querySelectorAll(
          'input[name="updates[' +
            id +
            ']"], ' +
            'a[href*="/cart/change?id=' +
            id +
            '"], ' +
            '[data-line-item-key*="' +
            id +
            '"]',
        )
        .forEach(function (el) {
          var row = el.closest(
            "tr, li, [class*='cart-item'], [class*='cart__item'], [class*='line-item']",
          );
          if (row) hideFeeControls(row);
        });
    });
  }

  function hideFeeControls(row) {
    // Hide remove / trash buttons
    row
      .querySelectorAll(
        'a[href*="/cart/change"], [class*="remove"], [class*="delete"], ' +
          '[class*="trash"], button[data-remove], [data-cart-remove], ' +
          ".cart-item--remove, .cart__remove, .remove-item",
      )
      .forEach(function (btn) {
        btn.style.display = "none";
      });

    // Hide quantity selectors (input + plus/minus buttons)
    row
      .querySelectorAll(
        'input[type="number"], .quantity-selector, .quantity, ' +
          '[class*="qty"], .cart-item--quantity, .cart__quantity-selector',
      )
      .forEach(function (el) {
        el.style.pointerEvents = "none";
        el.style.opacity = "0.5";
        el.tabIndex = -1;
        // For inputs, also make readonly
        if (el.tagName === "INPUT") {
          el.readOnly = true;
        }
      });

    // Mark the row so we don't process it twice
    row.setAttribute("data-fee-locked", "true");
  }

  // ─── BIND CHECKOUT BUTTON ──────────────────────────────────────────────────

  /**
   * Replace the native checkout button with a custom one that calls
   * handleCheckout directly — no form submit to race against.
   * Also block the form from submitting natively as a safety net.
   */
  function bindCheckoutButton() {
    // If we already created the custom button, nothing to do
    if (document.getElementById("custom-checkout-btn")) return;

    // Find the original checkout button
    var originalBtn =
      document.querySelector('[name="checkout"]') ||
      document.querySelector('button[type="submit"][form="cart-form"]') ||
      document.querySelector('#cart-form button[type="submit"]');

    if (originalBtn) {
      // Create custom button that matches the original's appearance
      var customBtn = document.createElement("button");
      customBtn.id = "custom-checkout-btn";
      customBtn.type = "button"; // NOT submit — won't trigger form submission
      customBtn.textContent = originalBtn.textContent || "Checkout";
      customBtn.className = originalBtn.className;

      // Copy computed styles that carry the theme's look
      var computed = window.getComputedStyle(originalBtn);
      customBtn.style.width = computed.width;
      customBtn.style.cursor = "pointer";

      customBtn.addEventListener("click", handleCheckout);

      // Replace the original button
      originalBtn.parentNode.insertBefore(customBtn, originalBtn);
      originalBtn.style.display = "none";
      originalBtn.setAttribute("data-fees-replaced", "true");

      console.log(
        "[CartFees] Replaced native checkout button with custom button",
      );
    }

    // Block the form from submitting natively (safety net for Enter key, etc.)
    var form = document.getElementById("cart-form");
    if (form && !form.hasAttribute("data-fees-bound")) {
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        e.stopPropagation();
        console.log(
          "[CartFees] Blocked native form submit — using custom checkout",
        );
        handleCheckout();
      });
      form.setAttribute("data-fees-bound", "true");
    }

    // Block direct checkout links outside the form
    document.querySelectorAll('a[href="/checkout"]').forEach(function (link) {
      if (!link.hasAttribute("data-fees-bound")) {
        link.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          console.log(
            "[CartFees] Blocked direct /checkout link — using custom checkout",
          );
          handleCheckout();
        });
        link.setAttribute("data-fees-bound", "true");
      }
    });

    // Hide accelerated/dynamic checkout buttons (Shop Pay, Apple Pay, Google Pay)
    hideAcceleratedCheckout();
  }

  /**
   * Hide dynamic/accelerated checkout buttons that bypass our fee logic.
   * These use Shopify's JS SDK and go directly to checkout.
   */
  function hideAcceleratedCheckout() {
    var selectors = [
      ".shopify-payment-button",
      ".additional-checkout-buttons",
      ".dynamic-checkout__buttons",
      '[data-shopify="dynamic-checkout"]',
      ".cart__dynamic-checkout-buttons",
      ".cart__ctas .additional-checkout-buttons",
    ];

    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (!el.hasAttribute("data-fees-hidden")) {
          el.style.display = "none";
          el.setAttribute("data-fees-hidden", "true");
          console.log("[CartFees] Hid accelerated checkout button:", sel);
        }
      });
    });
  }

  // ─── OBSERVE CART RE-RENDERS ────────────────────────────────────────────────

  function observeCartForm() {
    var target =
      document.getElementById("cart-form") ||
      document.querySelector('[action="/cart"]');
    if (!target) return;

    var section = target.closest(".shopify-section") || target.parentNode;
    var debounceTimer;

    var observer = new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        console.log(
          "[CartFees] Cart DOM changed — re-binding, re-locking, re-hiding",
        );
        bindCheckoutButton();
        lockFeeLineItems();
        hideAcceleratedCheckout();
      }, 100);
    });

    observer.observe(section, { childList: true, subtree: true });
  }

  // ─── BOOT ──────────────────────────────────────────────────────────────────

  function boot() {
    bindCheckoutButton();
    lockFeeLineItems();
    observeCartForm();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // ─── FETCH INTERCEPTOR (for _tags injection only) ──────────────────────────

  /**
   * Intercept /cart/add calls to inject _tags and _pallet_qty from hidden
   * form inputs. Measura builds its own payload and skips our form fields.
   */
  (function () {
    function getHiddenFormProps() {
      var props = {};
      var tagsInput = document.querySelector('input[name="properties[_tags]"]');
      var palletInput = document.querySelector(
        'input[name="properties[_pallet_qty]"]',
      );
      if (tagsInput && tagsInput.value) {
        props["_tags"] = tagsInput.value;
      }
      if (palletInput && palletInput.value) {
        props["_pallet_qty"] = palletInput.value;
      }
      return props;
    }

    var originalFetch = window.fetch;
    window.fetch = function (url, options) {
      if (
        typeof url === "string" &&
        url.includes("/cart/add") &&
        options &&
        options.body
      ) {
        try {
          var body =
            typeof options.body === "string"
              ? JSON.parse(options.body)
              : options.body;
          var formProps = getHiddenFormProps();
          var modified = false;

          if (body.items && Array.isArray(body.items)) {
            body.items.forEach(function (item) {
              if (ALL_FEE_VARIANT_IDS.indexOf(item.id) !== -1) return;
              if (!item.properties) item.properties = {};
              if (!item.properties["_tags"] && formProps["_tags"]) {
                item.properties["_tags"] = formProps["_tags"];
                modified = true;
              }
              if (!item.properties["_pallet_qty"] && formProps["_pallet_qty"]) {
                item.properties["_pallet_qty"] = formProps["_pallet_qty"];
                modified = true;
              }
            });
          }

          if (body.id && !body.items) {
            if (ALL_FEE_VARIANT_IDS.indexOf(body.id) === -1) {
              if (!body.properties) body.properties = {};
              if (!body.properties["_tags"] && formProps["_tags"]) {
                body.properties["_tags"] = formProps["_tags"];
                modified = true;
              }
              if (!body.properties["_pallet_qty"] && formProps["_pallet_qty"]) {
                body.properties["_pallet_qty"] = formProps["_pallet_qty"];
                modified = true;
              }
            }
          }

          if (modified) {
            console.log(
              "[CartFees] Injected missing properties into /cart/add:",
              formProps,
            );
            options = Object.assign({}, options, {
              body: JSON.stringify(body),
            });
          }
        } catch (e) {
          // Body wasn't JSON — leave it alone
        }
      }

      return originalFetch.call(this, url, options);
    };
  })();

  // ─── EXPOSE FOR DEBUGGING ──────────────────────────────────────────────────

  window.__cartFees = {
    calcTotalPallets: calcTotalPallets,
    calcDeliveryPallets: calcDeliveryPallets,
    calcBulkYardsByItem: calcBulkYardsByItem,
    handleCheckout: handleCheckout,
  };
})();

// /**
//  * CART FEE CALCULATOR — Checkout-time approach
//  * =============================================
//  * Calculates and adds fee line items when the user clicks Checkout.
//  * No background syncing, no reload loops, no Measura conflicts.
//  *
//  * 1. PALLET FEE ($35 per pallet)
//  *    - Applies to ALL products with a _pallet_qty line item property
//  *    - Pallets per product = Math.ceil(quantity / pallet_qty)
//  *    - Fee quantity = total pallets across all qualifying products
//  *
//  * 2. FLATBED DELIVERY FEE ($175 per 10 pallets)
//  *    - Only products tagged "Landscape Bag" or "Concrete Hardscapes"
//  *    - If _measura_amount or "Net volume" exists: spots = Math.ceil(parsed value)
//  *    - Else if _pallet_qty exists: spots = Math.ceil(quantity / pallet_qty)
//  *    - Fee quantity = Math.ceil(total spots / 10)
//  *
//  * 3. DUMP TRUCK BULK DELIVERY FEE (tiered by cubic yards)
//  *    - Products tagged "Bulk" — volume from _measura_amount or "Net volume" property
//  *    - ≤6 cu yd total: 1 × 1-6 Cubic Yards fee ($140)
//  *    - ≥7 cu yd total: Math.ceil(yards / 10) × 7-10 Cubic Yards fees ($150 each)
//  *
//  * Tags are embedded as a hidden _tags property at add-to-cart time.
//  * A fetch interceptor ensures Measura's add-to-cart calls also get _tags.
//  */

// (function () {
//   "use strict";

//   // ─── CONFIG ────────────────────────────────────────────────────────────────

//   const PALLET_FEE_VARIANT_ID = 51453890232603;

//   const DELIVERY_FEE_VARIANT_ID = 51788884803867;
//   const DELIVERY_FEE_TAGS = [
//     "landscape bag",
//     "concrete hardscapes",
//     "landscape accessories",
//     "accessories",
//     "artificial turf",
//   ];
//   const DELIVERY_PALLETS_PER_FEE = 10;

//   const DUMP_TRUCK_1_6_VARIANT_ID = 51795376177435; // $140
//   const DUMP_TRUCK_7_10_VARIANT_ID = 51987576946971; // $150
//   const BULK_TAG = "bulk";

//   const ALL_FEE_VARIANT_IDS = [
//     PALLET_FEE_VARIANT_ID,
//     DELIVERY_FEE_VARIANT_ID,
//     DUMP_TRUCK_1_6_VARIANT_ID,
//     DUMP_TRUCK_7_10_VARIANT_ID,
//   ];

//   // ─── CART API HELPERS ──────────────────────────────────────────────────────

//   function fetchCart() {
//     return fetch("/cart.js").then(function (r) {
//       return r.json();
//     });
//   }

//   function updateCartByKey(updates) {
//     return fetch("/cart/update.js", {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({ updates: updates }),
//     }).then(function (r) {
//       if (!r.ok) {
//         return r.json().then(function (body) {
//           var err = new Error(
//             "[CartFees] /cart/update.js " +
//               r.status +
//               ": " +
//               (body.description || body.message || JSON.stringify(body)),
//           );
//           console.error(err.message);
//           throw err;
//         });
//       }
//       return r.json();
//     });
//   }

//   // ─── CALCULATION HELPERS ───────────────────────────────────────────────────

//   function calcTotalPallets(items) {
//     let total = 0;
//     items.forEach(function (item) {
//       if (ALL_FEE_VARIANT_IDS.indexOf(item.variant_id) !== -1) return;
//       const palletQty = parseInt((item.properties || {})["_pallet_qty"], 10);
//       if (!isNaN(palletQty) && palletQty > 0) {
//         total += Math.ceil(item.quantity / palletQty);
//       }
//     });
//     return total;
//   }

//   function calcDeliveryPallets(items) {
//     let total = 0;
//     items.forEach(function (item) {
//       if (ALL_FEE_VARIANT_IDS.indexOf(item.variant_id) !== -1) return;

//       const props = item.properties || {};
//       const rawTags = (props["_tags"] || "").toLowerCase();
//       const hasDeliveryTag = DELIVERY_FEE_TAGS.some(function (tag) {
//         return rawTags.indexOf(tag) !== -1;
//       });

//       console.log(
//         "[CartFees] calcDeliveryPallets item:",
//         item.title,
//         "| qty:", item.quantity,
//         "| _tags:", props["_tags"],
//         "| hasDeliveryTag:", hasDeliveryTag,
//         "| _measura_amount:", props["_measura_amount"],
//         "| _pallet_qty:", props["_pallet_qty"],
//         "| all props:", props,
//       );

//       if (!hasDeliveryTag) return;

//       // Measura stores total volume in _measura_amount or a visible "Net volume" property
//       const measuraAmount = props["_measura_amount"] || props["Net volume"];
//       if (measuraAmount) {
//         const parsed = parseFloat(measuraAmount);
//         if (!isNaN(parsed) && parsed > 0) {
//           total += Math.ceil(parsed);
//           return;
//         }
//       }

//       // Fall back to pallet calculation (e.g. Concrete Hardscapes)
//       const palletQty = parseInt(props["_pallet_qty"], 10);
//       if (!isNaN(palletQty) && palletQty > 0) {
//         total += Math.ceil(item.quantity / palletQty);
//         return;
//       }

//       // No _measura_amount or _pallet_qty — each unit = 1 spot (e.g. Landscape Bags)
//       total += Math.ceil(item.quantity);
//     });
//     console.log("[CartFees] calcDeliveryPallets total spots:", total);
//     return total;
//   }

//   function calcBulkYards(items) {
//     let total = 0;
//     items.forEach(function (item) {
//       if (ALL_FEE_VARIANT_IDS.indexOf(item.variant_id) !== -1) return;

//       const props = item.properties || {};
//       const rawTags = (props["_tags"] || "").toLowerCase();

//       if (rawTags.indexOf(BULK_TAG) !== -1) {
//         const measuraAmount = props["_measura_amount"] || props["Net volume"];
//         if (measuraAmount) {
//           const parsed = parseFloat(measuraAmount);
//           if (!isNaN(parsed) && parsed > 0) {
//             total += parsed;
//           }
//         } else {
//           total += item.quantity;
//         }
//       }
//     });
//     return total;
//   }

//   // ─── CHECKOUT HANDLER ──────────────────────────────────────────────────────

//   /**
//    * Intercept the checkout button. Calculate fees, update the cart, then
//    * redirect to /checkout. Runs once per click — no loops, no reloads.
//    */
//   function handleCheckout() {
//     var btn = document.getElementById("custom-checkout-btn");
//     if (!btn || btn.disabled) return;

//     // Disable the button while processing
//     btn.disabled = true;
//     btn.textContent = "Calculating fees…";
//     btn.style.opacity = "0.6";
//     btn.style.pointerEvents = "none";
//     clearCheckoutError();

//     console.log("[CartFees] Checkout clicked — calculating fees…");

//     fetchCart()
//       .then(function (cart) {
//         var items = cart.items || [];

//         // --- Calculate all fees ---
//         var totalPallets = calcTotalPallets(items);
//         var palletFeeQty = totalPallets;

//         var deliveryPallets = calcDeliveryPallets(items);
//         var deliveryFeeQty =
//           deliveryPallets > 0
//             ? Math.ceil(deliveryPallets / DELIVERY_PALLETS_PER_FEE)
//             : 0;

//         var bulkYards = calcBulkYards(items);
//         var dumpTruck1_6Qty = 0;
//         var dumpTruck7_10Qty = 0;

//         if (bulkYards > 0) {
//           if (bulkYards <= 6) {
//             dumpTruck1_6Qty = 1;
//           } else {
//             dumpTruck7_10Qty = Math.ceil(bulkYards / 10);
//           }
//         }

//         console.log(
//           "[CartFees] Pallets:",
//           totalPallets,
//           "→ pallet fee:",
//           palletFeeQty,
//           "| Delivery pallets:",
//           deliveryPallets,
//           "→ delivery fee:",
//           deliveryFeeQty,
//           "| Bulk yards:",
//           bulkYards,
//           "→ dump 1-6:",
//           dumpTruck1_6Qty,
//           "dump 7-10:",
//           dumpTruck7_10Qty,
//         );

//         // --- Single update: set all fee quantities (0 = remove) ---
//         var updates = {};
//         updates[PALLET_FEE_VARIANT_ID] = palletFeeQty;
//         updates[DELIVERY_FEE_VARIANT_ID] = deliveryFeeQty;
//         updates[DUMP_TRUCK_1_6_VARIANT_ID] = dumpTruck1_6Qty;
//         updates[DUMP_TRUCK_7_10_VARIANT_ID] = dumpTruck7_10Qty;

//         return updateCartByKey(updates);
//       })
//       .then(function () {
//         console.log("[CartFees] Fees synced — redirecting in 5s (debug mode)");
//         setTimeout(function () { window.location.href = "/checkout"; }, 5000);
//       })
//       .catch(function (err) {
//         console.error("[CartFees] Error syncing fees at checkout:", err);
//         // Re-enable button so user can retry — do NOT redirect without fees
//         btn.disabled = false;
//         btn.textContent = "Checkout";
//         btn.style.opacity = "";
//         btn.style.pointerEvents = "";
//         showCheckoutError(
//           "There was a problem preparing your order. Please try again.",
//         );
//       });
//   }

//   function showCheckoutError(msg) {
//     clearCheckoutError();
//     var el = document.createElement("div");
//     el.id = "cart-fees-error";
//     el.setAttribute("role", "alert");
//     el.style.cssText =
//       "color:#cc0000;font-size:14px;margin-top:10px;text-align:center;";
//     el.textContent = msg;
//     var btn = document.getElementById("custom-checkout-btn");
//     if (btn && btn.parentNode) {
//       btn.parentNode.insertBefore(el, btn.nextSibling);
//     }
//   }

//   function clearCheckoutError() {
//     var existing = document.getElementById("cart-fees-error");
//     if (existing) existing.remove();
//   }

//   // ─── HIDE FEE CONTROLS ON CART PAGE ─────────────────────────────────────────

//   /**
//    * On the cart page, hide the remove/trash buttons and quantity inputs for
//    * fee line items so users cannot manually delete or change them.
//    * Fees are fully managed by handleCheckout — recalculated on every click.
//    */
//   function lockFeeLineItems() {
//     // Build a set of fee variant ID strings for quick lookup
//     var feeIdSet = {};
//     ALL_FEE_VARIANT_IDS.forEach(function (id) {
//       feeIdSet[String(id)] = true;
//     });

//     // Strategy 1: Find elements with data-variant-id matching a fee
//     document.querySelectorAll("[data-variant-id]").forEach(function (el) {
//       if (feeIdSet[el.getAttribute("data-variant-id")]) {
//         hideFeeControls(el);
//       }
//     });

//     // Strategy 2: Find cart item links whose href contains the fee variant ID
//     ALL_FEE_VARIANT_IDS.forEach(function (id) {
//       document
//         .querySelectorAll('a[href*="' + id + '"]')
//         .forEach(function (link) {
//           var row = link.closest(
//             "tr, li, [class*='cart-item'], [class*='cart__item'], [class*='line-item']",
//           );
//           if (row) hideFeeControls(row);
//         });
//     });

//     // Strategy 3: Find remove buttons / quantity inputs whose name or value
//     // references a fee variant ID (Shopify uses updates[variant_id] pattern)
//     ALL_FEE_VARIANT_IDS.forEach(function (id) {
//       document
//         .querySelectorAll(
//           'input[name="updates[' +
//             id +
//             ']"], ' +
//             'a[href*="/cart/change?id=' +
//             id +
//             '"], ' +
//             '[data-line-item-key*="' +
//             id +
//             '"]',
//         )
//         .forEach(function (el) {
//           var row = el.closest(
//             "tr, li, [class*='cart-item'], [class*='cart__item'], [class*='line-item']",
//           );
//           if (row) hideFeeControls(row);
//         });
//     });
//   }

//   function hideFeeControls(row) {
//     // Hide remove / trash buttons
//     row
//       .querySelectorAll(
//         'a[href*="/cart/change"], [class*="remove"], [class*="delete"], ' +
//           '[class*="trash"], button[data-remove], [data-cart-remove], ' +
//           ".cart-item--remove, .cart__remove, .remove-item",
//       )
//       .forEach(function (btn) {
//         btn.style.display = "none";
//       });

//     // Hide quantity selectors (input + plus/minus buttons)
//     row
//       .querySelectorAll(
//         'input[type="number"], .quantity-selector, .quantity, ' +
//           '[class*="qty"], .cart-item--quantity, .cart__quantity-selector',
//       )
//       .forEach(function (el) {
//         el.style.pointerEvents = "none";
//         el.style.opacity = "0.5";
//         el.tabIndex = -1;
//         // For inputs, also make readonly
//         if (el.tagName === "INPUT") {
//           el.readOnly = true;
//         }
//       });

//     // Mark the row so we don't process it twice
//     row.setAttribute("data-fee-locked", "true");
//   }

//   // ─── BIND CHECKOUT BUTTON ──────────────────────────────────────────────────

//   /**
//    * Replace the native checkout button with a custom one that calls
//    * handleCheckout directly — no form submit to race against.
//    * Also block the form from submitting natively as a safety net.
//    */
//   function bindCheckoutButton() {
//     // If we already created the custom button, nothing to do
//     if (document.getElementById("custom-checkout-btn")) return;

//     // Find the original checkout button
//     var originalBtn =
//       document.querySelector('[name="checkout"]') ||
//       document.querySelector('button[type="submit"][form="cart-form"]') ||
//       document.querySelector('#cart-form button[type="submit"]');

//     if (originalBtn) {
//       // Create custom button that matches the original's appearance
//       var customBtn = document.createElement("button");
//       customBtn.id = "custom-checkout-btn";
//       customBtn.type = "button"; // NOT submit — won't trigger form submission
//       customBtn.textContent = originalBtn.textContent || "Checkout";
//       customBtn.className = originalBtn.className;

//       // Copy computed styles that carry the theme's look
//       var computed = window.getComputedStyle(originalBtn);
//       customBtn.style.width = computed.width;
//       customBtn.style.cursor = "pointer";

//       customBtn.addEventListener("click", handleCheckout);

//       // Replace the original button
//       originalBtn.parentNode.insertBefore(customBtn, originalBtn);
//       originalBtn.style.display = "none";
//       originalBtn.setAttribute("data-fees-replaced", "true");

//       console.log(
//         "[CartFees] Replaced native checkout button with custom button",
//       );
//     }

//     // Block the form from submitting natively (safety net for Enter key, etc.)
//     var form = document.getElementById("cart-form");
//     if (form && !form.hasAttribute("data-fees-bound")) {
//       form.addEventListener("submit", function (e) {
//         e.preventDefault();
//         e.stopPropagation();
//         console.log(
//           "[CartFees] Blocked native form submit — using custom checkout",
//         );
//         handleCheckout();
//       });
//       form.setAttribute("data-fees-bound", "true");
//     }

//     // Block direct checkout links outside the form
//     document.querySelectorAll('a[href="/checkout"]').forEach(function (link) {
//       if (!link.hasAttribute("data-fees-bound")) {
//         link.addEventListener("click", function (e) {
//           e.preventDefault();
//           e.stopPropagation();
//           console.log(
//             "[CartFees] Blocked direct /checkout link — using custom checkout",
//           );
//           handleCheckout();
//         });
//         link.setAttribute("data-fees-bound", "true");
//       }
//     });

//     // Hide accelerated/dynamic checkout buttons (Shop Pay, Apple Pay, Google Pay)
//     hideAcceleratedCheckout();
//   }

//   /**
//    * Hide dynamic/accelerated checkout buttons that bypass our fee logic.
//    * These use Shopify's JS SDK and go directly to checkout.
//    */
//   function hideAcceleratedCheckout() {
//     var selectors = [
//       ".shopify-payment-button",
//       ".additional-checkout-buttons",
//       ".dynamic-checkout__buttons",
//       '[data-shopify="dynamic-checkout"]',
//       ".cart__dynamic-checkout-buttons",
//       ".cart__ctas .additional-checkout-buttons",
//     ];

//     selectors.forEach(function (sel) {
//       document.querySelectorAll(sel).forEach(function (el) {
//         if (!el.hasAttribute("data-fees-hidden")) {
//           el.style.display = "none";
//           el.setAttribute("data-fees-hidden", "true");
//           console.log("[CartFees] Hid accelerated checkout button:", sel);
//         }
//       });
//     });
//   }

//   // ─── OBSERVE CART RE-RENDERS ────────────────────────────────────────────────

//   function observeCartForm() {
//     var target =
//       document.getElementById("cart-form") ||
//       document.querySelector('[action="/cart"]');
//     if (!target) return;

//     var section = target.closest(".shopify-section") || target.parentNode;
//     var debounceTimer;

//     var observer = new MutationObserver(function () {
//       clearTimeout(debounceTimer);
//       debounceTimer = setTimeout(function () {
//         console.log(
//           "[CartFees] Cart DOM changed — re-binding, re-locking, re-hiding",
//         );
//         bindCheckoutButton();
//         lockFeeLineItems();
//         hideAcceleratedCheckout();
//       }, 100);
//     });

//     observer.observe(section, { childList: true, subtree: true });
//   }

//   // ─── BOOT ──────────────────────────────────────────────────────────────────

//   function boot() {
//     bindCheckoutButton();
//     lockFeeLineItems();
//     observeCartForm();
//   }

//   if (document.readyState === "loading") {
//     document.addEventListener("DOMContentLoaded", boot);
//   } else {
//     boot();
//   }

//   // ─── FETCH INTERCEPTOR (for _tags injection only) ──────────────────────────

//   /**
//    * Intercept /cart/add calls to inject _tags and _pallet_qty from hidden
//    * form inputs. Measura builds its own payload and skips our form fields.
//    */
//   (function () {
//     function getHiddenFormProps() {
//       var props = {};
//       var tagsInput = document.querySelector('input[name="properties[_tags]"]');
//       var palletInput = document.querySelector(
//         'input[name="properties[_pallet_qty]"]',
//       );
//       if (tagsInput && tagsInput.value) {
//         props["_tags"] = tagsInput.value;
//       }
//       if (palletInput && palletInput.value) {
//         props["_pallet_qty"] = palletInput.value;
//       }
//       return props;
//     }

//     var originalFetch = window.fetch;
//     window.fetch = function (url, options) {
//       if (
//         typeof url === "string" &&
//         url.includes("/cart/add") &&
//         options &&
//         options.body
//       ) {
//         try {
//           var body =
//             typeof options.body === "string"
//               ? JSON.parse(options.body)
//               : options.body;
//           var formProps = getHiddenFormProps();
//           var modified = false;

//           if (body.items && Array.isArray(body.items)) {
//             body.items.forEach(function (item) {
//               if (ALL_FEE_VARIANT_IDS.indexOf(item.id) !== -1) return;
//               if (!item.properties) item.properties = {};
//               if (!item.properties["_tags"] && formProps["_tags"]) {
//                 item.properties["_tags"] = formProps["_tags"];
//                 modified = true;
//               }
//               if (!item.properties["_pallet_qty"] && formProps["_pallet_qty"]) {
//                 item.properties["_pallet_qty"] = formProps["_pallet_qty"];
//                 modified = true;
//               }
//             });
//           }

//           if (body.id && !body.items) {
//             if (ALL_FEE_VARIANT_IDS.indexOf(body.id) === -1) {
//               if (!body.properties) body.properties = {};
//               if (!body.properties["_tags"] && formProps["_tags"]) {
//                 body.properties["_tags"] = formProps["_tags"];
//                 modified = true;
//               }
//               if (!body.properties["_pallet_qty"] && formProps["_pallet_qty"]) {
//                 body.properties["_pallet_qty"] = formProps["_pallet_qty"];
//                 modified = true;
//               }
//             }
//           }

//           if (modified) {
//             console.log(
//               "[CartFees] Injected missing properties into /cart/add:",
//               formProps,
//             );
//             options = Object.assign({}, options, {
//               body: JSON.stringify(body),
//             });
//           }
//         } catch (e) {
//           // Body wasn't JSON — leave it alone
//         }
//       }

//       return originalFetch.call(this, url, options);
//     };
//   })();

//   // ─── EXPOSE FOR DEBUGGING ──────────────────────────────────────────────────

//   window.__cartFees = {
//     calcTotalPallets: calcTotalPallets,
//     calcDeliveryPallets: calcDeliveryPallets,
//     calcBulkYards: calcBulkYards,
//     handleCheckout: handleCheckout,
//   };
// })();




/*

OLD CODE CONTENT


*/






// (function () {
//   "use strict";

//   // ─── CONFIG ────────────────────────────────────────────────────────────────

//   const PALLET_FEE_VARIANT_ID = 51453890232603;

//   const DELIVERY_FEE_VARIANT_ID = 51788884803867;
//   const DELIVERY_FEE_TAGS = ["landscape bag", "concrete hardscapes", "landscape accessories", "accessories", "artificial turf"];
//   const DELIVERY_PALLETS_PER_FEE = 10;

//   const DUMP_TRUCK_1_6_VARIANT_ID = 51795376177435; // $140
//   const DUMP_TRUCK_7_10_VARIANT_ID = 51987576946971; // $150
//   const BULK_TAG = "bulk";

//   const ALL_FEE_VARIANT_IDS = [
//     PALLET_FEE_VARIANT_ID,
//     DELIVERY_FEE_VARIANT_ID,
//     DUMP_TRUCK_1_6_VARIANT_ID,
//     DUMP_TRUCK_7_10_VARIANT_ID,
//   ];

//   // ─── CART API HELPERS ──────────────────────────────────────────────────────

//   function fetchCart() {
//     return fetch("/cart.js").then(function (r) {
//       return r.json();
//     });
//   }

//   function updateCartByKey(updates) {
//     return fetch("/cart/update.js", {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({ updates: updates }),
//     }).then(function (r) {
//       if (!r.ok) {
//         return r.json().then(function (body) {
//           var err = new Error("[CartFees] /cart/update.js " + r.status + ": " + (body.description || body.message || JSON.stringify(body)));
//           console.error(err.message);
//           throw err;
//         });
//       }
//       return r.json();
//     });
//   }

//   // ─── CALCULATION HELPERS ───────────────────────────────────────────────────

//   function calcTotalPallets(items) {
//     let total = 0;
//     items.forEach(function (item) {
//       if (ALL_FEE_VARIANT_IDS.indexOf(item.variant_id) !== -1) return;
//       const palletQty = parseInt((item.properties || {})["_pallet_qty"], 10);
//       if (!isNaN(palletQty) && palletQty > 0) {
//         total += Math.ceil(item.quantity / palletQty);
//       }
//     });
//     return total;
//   }

//   function calcDeliveryPallets(items) {
//     let total = 0;
//     items.forEach(function (item) {
//       if (ALL_FEE_VARIANT_IDS.indexOf(item.variant_id) !== -1) return;

//       const props = item.properties || {};
//       const rawTags = (props["_tags"] || "").toLowerCase();
//       const hasDeliveryTag = DELIVERY_FEE_TAGS.some(function (tag) {
//         return rawTags.indexOf(tag) !== -1;
//       });

//       if (!hasDeliveryTag) return;

//       // Prefer _measura_amount (e.g. Landscape Bags sold by fractional qty)
//       const measuraAmount = props["_measura_amount"];
//       if (measuraAmount) {
//         const parsed = parseFloat(measuraAmount);
//         if (!isNaN(parsed) && parsed > 0) {
//           total += Math.ceil(parsed);
//           return;
//         }
//       }

//       // Fall back to pallet calculation (e.g. Concrete Hardscapes)
//       const palletQty = parseInt(props["_pallet_qty"], 10);
//       if (!isNaN(palletQty) && palletQty > 0) {
//         total += Math.ceil(item.quantity / palletQty);
//         return;
//       }

//       // No _measura_amount or _pallet_qty — each unit = 1 spot (e.g. Landscape Bags)
//       total += Math.ceil(item.quantity);
//     });
//     return total;
//   }

//   function calcBulkYards(items) {
//     let total = 0;
//     items.forEach(function (item) {
//       if (ALL_FEE_VARIANT_IDS.indexOf(item.variant_id) !== -1) return;

//       const props = item.properties || {};
//       const rawTags = (props["_tags"] || "").toLowerCase();

//       if (rawTags.indexOf(BULK_TAG) !== -1) {
//         const measuraAmount = props["_measura_amount"];
//         if (measuraAmount) {
//           const parsed = parseFloat(measuraAmount);
//           if (!isNaN(parsed) && parsed > 0) {
//             total += parsed;
//           }
//         } else {
//           total += item.quantity;
//         }
//       }
//     });
//     return total;
//   }

//   // ─── CHECKOUT HANDLER ──────────────────────────────────────────────────────

//   /**
//    * Intercept the checkout button. Calculate fees, update the cart, then
//    * redirect to /checkout. Runs once per click — no loops, no reloads.
//    */
//   function handleCheckout(e) {
//     e.preventDefault();
//     e.stopPropagation();

//     // Disable the checkout button visually while processing
//     var btn = document.querySelector('[name="checkout"]') || e.currentTarget;
//     btn.disabled = true;
//     btn.style.opacity = "0.6";
//     btn.style.pointerEvents = "none";

//     console.log("[CartFees] Checkout clicked — calculating fees…");

//     fetchCart()
//       .then(function (cart) {
//         var items = cart.items || [];

//         // --- Calculate all fees ---
//         var totalPallets = calcTotalPallets(items);
//         var palletFeeQty = totalPallets;

//         var deliveryPallets = calcDeliveryPallets(items);
//         var deliveryFeeQty =
//           deliveryPallets > 0
//             ? Math.ceil(deliveryPallets / DELIVERY_PALLETS_PER_FEE)
//             : 0;

//         var bulkYards = calcBulkYards(items);
//         var dumpTruck1_6Qty = 0;
//         var dumpTruck7_10Qty = 0;

//         if (bulkYards > 0) {
//           var remaining = bulkYards;
//           while (remaining >= 7) {
//             dumpTruck7_10Qty += 1;
//             remaining -= 10;
//           }
//           if (remaining > 0) {
//             dumpTruck1_6Qty += 1;
//           }
//         }

//         console.log(
//           "[CartFees] Pallets:",
//           totalPallets,
//           "→ pallet fee:",
//           palletFeeQty,
//           "| Delivery pallets:",
//           deliveryPallets,
//           "→ delivery fee:",
//           deliveryFeeQty,
//           "| Bulk yards:",
//           bulkYards,
//           "→ dump 1-6:",
//           dumpTruck1_6Qty,
//           "dump 7-10:",
//           dumpTruck7_10Qty,
//         );

//         // --- Single update: set all fee quantities (0 = remove) ---
//         var updates = {};
//         updates[PALLET_FEE_VARIANT_ID] = palletFeeQty;
//         updates[DELIVERY_FEE_VARIANT_ID] = deliveryFeeQty;
//         updates[DUMP_TRUCK_1_6_VARIANT_ID] = dumpTruck1_6Qty;
//         updates[DUMP_TRUCK_7_10_VARIANT_ID] = dumpTruck7_10Qty;

//         return updateCartByKey(updates);
//       })
//       .then(function () {
//         console.log("[CartFees] Fees synced — redirecting to checkout");
//         window.location.href = "/checkout";
//       })
//       .catch(function (err) {
//         console.error("[CartFees] Error syncing fees at checkout:", err);
//         // Re-enable button so user can retry
//         btn.disabled = false;
//         btn.style.opacity = "";
//         btn.style.pointerEvents = "";
//         // Fall through to checkout anyway so the user isn't stuck
//         window.location.href = "/checkout";
//       });
//   }

//   // ─── HIDE FEE CONTROLS ON CART PAGE ─────────────────────────────────────────

//   /**
//    * On the cart page, hide the remove/trash buttons and quantity inputs for
//    * fee line items so users cannot manually delete or change them.
//    * Fees are fully managed by handleCheckout — recalculated on every click.
//    */
//   function lockFeeLineItems() {
//     // Build a set of fee variant ID strings for quick lookup
//     var feeIdSet = {};
//     ALL_FEE_VARIANT_IDS.forEach(function (id) {
//       feeIdSet[String(id)] = true;
//     });

//     // Strategy 1: Find elements with data-variant-id matching a fee
//     document.querySelectorAll("[data-variant-id]").forEach(function (el) {
//       if (feeIdSet[el.getAttribute("data-variant-id")]) {
//         hideFeeControls(el);
//       }
//     });

//     // Strategy 2: Find cart item links whose href contains the fee variant ID
//     ALL_FEE_VARIANT_IDS.forEach(function (id) {
//       document
//         .querySelectorAll('a[href*="' + id + '"]')
//         .forEach(function (link) {
//           var row = link.closest(
//             "tr, li, [class*='cart-item'], [class*='cart__item'], [class*='line-item']",
//           );
//           if (row) hideFeeControls(row);
//         });
//     });

//     // Strategy 3: Find remove buttons / quantity inputs whose name or value
//     // references a fee variant ID (Shopify uses updates[variant_id] pattern)
//     ALL_FEE_VARIANT_IDS.forEach(function (id) {
//       document
//         .querySelectorAll(
//           'input[name="updates[' +
//             id +
//             ']"], ' +
//             'a[href*="/cart/change?id=' +
//             id +
//             '"], ' +
//             '[data-line-item-key*="' +
//             id +
//             '"]',
//         )
//         .forEach(function (el) {
//           var row = el.closest(
//             "tr, li, [class*='cart-item'], [class*='cart__item'], [class*='line-item']",
//           );
//           if (row) hideFeeControls(row);
//         });
//     });
//   }

//   function hideFeeControls(row) {
//     // Hide remove / trash buttons
//     row
//       .querySelectorAll(
//         'a[href*="/cart/change"], [class*="remove"], [class*="delete"], ' +
//           '[class*="trash"], button[data-remove], [data-cart-remove], ' +
//           ".cart-item--remove, .cart__remove, .remove-item",
//       )
//       .forEach(function (btn) {
//         btn.style.display = "none";
//       });

//     // Hide quantity selectors (input + plus/minus buttons)
//     row
//       .querySelectorAll(
//         'input[type="number"], .quantity-selector, .quantity, ' +
//           '[class*="qty"], .cart-item--quantity, .cart__quantity-selector',
//       )
//       .forEach(function (el) {
//         el.style.pointerEvents = "none";
//         el.style.opacity = "0.5";
//         el.tabIndex = -1;
//         // For inputs, also make readonly
//         if (el.tagName === "INPUT") {
//           el.readOnly = true;
//         }
//       });

//     // Mark the row so we don't process it twice
//     row.setAttribute("data-fee-locked", "true");
//   }

//   // ─── BIND CHECKOUT BUTTON ──────────────────────────────────────────────────

//   function bindCheckoutButton() {
//     // The checkout button is type="submit" name="checkout" with form="cart-form",
//     // so we intercept the form's submit event to catch all submit paths
//     // (button click, Enter key, dynamic checkout buttons, etc.)
//     var form = document.getElementById("cart-form");
//     if (form && !form.hasAttribute("data-fees-bound")) {
//       form.addEventListener("submit", handleCheckout);
//       form.setAttribute("data-fees-bound", "true");
//       console.log("[CartFees] Bound checkout interceptor to #cart-form submit");
//     }

//     // Also catch direct checkout links/buttons outside the form
//     var buttons = document.querySelectorAll('a[href="/checkout"]');
//     buttons.forEach(function (btn) {
//       if (!btn.hasAttribute("data-fees-bound")) {
//         btn.addEventListener("click", handleCheckout);
//         btn.setAttribute("data-fees-bound", "true");
//       }
//     });
//   }

//   // ─── OBSERVE CART RE-RENDERS ────────────────────────────────────────────────

//   function observeCartForm() {
//     var target =
//       document.getElementById("cart-form") ||
//       document.querySelector('[action="/cart"]');
//     if (!target) return;

//     var section = target.closest(".shopify-section") || target.parentNode;
//     var debounceTimer;

//     var observer = new MutationObserver(function () {
//       clearTimeout(debounceTimer);
//       debounceTimer = setTimeout(function () {
//         console.log("[CartFees] Cart DOM changed — re-binding and re-locking");
//         bindCheckoutButton();
//         lockFeeLineItems();
//       }, 100);
//     });

//     observer.observe(section, { childList: true, subtree: true });
//   }

//   // ─── BOOT ──────────────────────────────────────────────────────────────────

//   function boot() {
//     bindCheckoutButton();
//     lockFeeLineItems();
//     observeCartForm();
//   }

//   if (document.readyState === "loading") {
//     document.addEventListener("DOMContentLoaded", boot);
//   } else {
//     boot();
//   }

//   // ─── FETCH INTERCEPTOR (for _tags injection only) ──────────────────────────

//   /**
//    * Intercept /cart/add calls to inject _tags and _pallet_qty from hidden
//    * form inputs. Measura builds its own payload and skips our form fields.
//    */
//   (function () {
//     function getHiddenFormProps() {
//       var props = {};
//       var tagsInput = document.querySelector('input[name="properties[_tags]"]');
//       var palletInput = document.querySelector(
//         'input[name="properties[_pallet_qty]"]',
//       );
//       if (tagsInput && tagsInput.value) {
//         props["_tags"] = tagsInput.value;
//       }
//       if (palletInput && palletInput.value) {
//         props["_pallet_qty"] = palletInput.value;
//       }
//       return props;
//     }

//     var originalFetch = window.fetch;
//     window.fetch = function (url, options) {
//       if (
//         typeof url === "string" &&
//         url.includes("/cart/add") &&
//         options &&
//         options.body
//       ) {
//         try {
//           var body =
//             typeof options.body === "string"
//               ? JSON.parse(options.body)
//               : options.body;
//           var formProps = getHiddenFormProps();
//           var modified = false;

//           if (body.items && Array.isArray(body.items)) {
//             body.items.forEach(function (item) {
//               if (ALL_FEE_VARIANT_IDS.indexOf(item.id) !== -1) return;
//               if (!item.properties) item.properties = {};
//               if (!item.properties["_tags"] && formProps["_tags"]) {
//                 item.properties["_tags"] = formProps["_tags"];
//                 modified = true;
//               }
//               if (!item.properties["_pallet_qty"] && formProps["_pallet_qty"]) {
//                 item.properties["_pallet_qty"] = formProps["_pallet_qty"];
//                 modified = true;
//               }
//             });
//           }

//           if (body.id && !body.items) {
//             if (ALL_FEE_VARIANT_IDS.indexOf(body.id) === -1) {
//               if (!body.properties) body.properties = {};
//               if (!body.properties["_tags"] && formProps["_tags"]) {
//                 body.properties["_tags"] = formProps["_tags"];
//                 modified = true;
//               }
//               if (!body.properties["_pallet_qty"] && formProps["_pallet_qty"]) {
//                 body.properties["_pallet_qty"] = formProps["_pallet_qty"];
//                 modified = true;
//               }
//             }
//           }

//           if (modified) {
//             console.log(
//               "[CartFees] Injected missing properties into /cart/add:",
//               formProps,
//             );
//             options = Object.assign({}, options, {
//               body: JSON.stringify(body),
//             });
//           }
//         } catch (e) {
//           // Body wasn't JSON — leave it alone
//         }
//       }

//       return originalFetch.call(this, url, options);
//     };
//   })();

//   // ─── EXPOSE FOR DEBUGGING ──────────────────────────────────────────────────

//   window.__cartFees = {
//     calcTotalPallets: calcTotalPallets,
//     calcDeliveryPallets: calcDeliveryPallets,
//     calcBulkYards: calcBulkYards,
//     handleCheckout: handleCheckout,
//   };
// })();



// /**
//  * CART FEE CALCULATOR — Checkout-time approach

//OLD 


//  * =============================================
//  * Calculates and adds fee line items when the user clicks Checkout.
//  * No background syncing, no reload loops, no Measura conflicts.
//  *
//  * 1. PALLET FEE ($35 per pallet)
//  *    - Applies to ALL products with a _pallet_qty line item property
//  *    - Pallets per product = Math.ceil(quantity / pallet_qty)
//  *    - Fee quantity = total pallets across all qualifying products
//  *
//  * 2. FLATBED DELIVERY FEE ($175 per 10 pallets)
//  *    - Only products tagged "Landscape Bag" or "Concrete Hardscapes"
//  *    - If _measura_amount exists: spots = Math.ceil(_measura_amount)
//  *    - Else if _pallet_qty exists: spots = Math.ceil(quantity / pallet_qty)
//  *    - Fee quantity = Math.ceil(total spots / 10)
//  *
//  * 3. DUMP TRUCK BULK DELIVERY FEE (tiered by cubic yards)
//  *    - Products tagged "Bulk" — volume from Measura's _measura_amount
//  *    - $150 per 7-10 yard load, $140 per 1-6 yard load
//  *    - Greedy fill: pack 10-yard chunks first, remainder gets 1 small chunk
//  *
//  * Tags are embedded as a hidden _tags property at add-to-cart time.
//  * A fetch interceptor ensures Measura's add-to-cart calls also get _tags.
//  */

// (function () {
//   "use strict";

//   // ─── CONFIG ────────────────────────────────────────────────────────────────

//   const PALLET_FEE_VARIANT_ID = 51453890232603;

//   const DELIVERY_FEE_VARIANT_ID = 51788884803867;
//   const DELIVERY_FEE_TAGS = ["landscape bag", "concrete hardscapes", "landscape accessories", "accessories", "artificial turf"];
//   const DELIVERY_PALLETS_PER_FEE = 10;

//   const DUMP_TRUCK_1_6_VARIANT_ID = 51795376177435; // $140
//   const DUMP_TRUCK_7_10_VARIANT_ID = 51795376210203; // $150
//   const BULK_TAG = "bulk";

//   const ALL_FEE_VARIANT_IDS = [
//     PALLET_FEE_VARIANT_ID,
//     DELIVERY_FEE_VARIANT_ID,
//     DUMP_TRUCK_1_6_VARIANT_ID,
//     DUMP_TRUCK_7_10_VARIANT_ID,
//   ];

//   // ─── CART API HELPERS ──────────────────────────────────────────────────────

//   function fetchCart() {
//     return fetch("/cart.js").then(function (r) {
//       return r.json();
//     });
//   }

//   function updateCartByKey(updates) {
//     return fetch("/cart/update.js", {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({ updates: updates }),
//     }).then(function (r) {
//       return r.json();
//     });
//   }

//   // ─── CALCULATION HELPERS ───────────────────────────────────────────────────

//   function calcTotalPallets(items) {
//     let total = 0;
//     items.forEach(function (item) {
//       if (ALL_FEE_VARIANT_IDS.indexOf(item.variant_id) !== -1) return;
//       const palletQty = parseInt((item.properties || {})["_pallet_qty"], 10);
//       if (!isNaN(palletQty) && palletQty > 0) {
//         total += Math.ceil(item.quantity / palletQty);
//       }
//     });
//     return total;
//   }

//   function calcDeliveryPallets(items) {
//     let total = 0;
//     items.forEach(function (item) {
//       if (ALL_FEE_VARIANT_IDS.indexOf(item.variant_id) !== -1) return;

//       const props = item.properties || {};
//       const rawTags = (props["_tags"] || "").toLowerCase();
//       const hasDeliveryTag = DELIVERY_FEE_TAGS.some(function (tag) {
//         return rawTags.indexOf(tag) !== -1;
//       });

//       if (!hasDeliveryTag) return;

//       // Prefer _measura_amount (e.g. Landscape Bags sold by fractional qty)
//       const measuraAmount = props["_measura_amount"];
//       if (measuraAmount) {
//         const parsed = parseFloat(measuraAmount);
//         if (!isNaN(parsed) && parsed > 0) {
//           total += Math.ceil(parsed);
//           return;
//         }
//       }

//       // Fall back to pallet calculation (e.g. Concrete Hardscapes)
//       const palletQty = parseInt(props["_pallet_qty"], 10);
//       if (!isNaN(palletQty) && palletQty > 0) {
//         total += Math.ceil(item.quantity / palletQty);
//         return;
//       }

//       // No _measura_amount or _pallet_qty — each unit = 1 spot (e.g. Landscape Bags)
//       total += Math.ceil(item.quantity);
//     });
//     return total;
//   }

//   function calcBulkYards(items) {
//     let total = 0;
//     items.forEach(function (item) {
//       if (ALL_FEE_VARIANT_IDS.indexOf(item.variant_id) !== -1) return;

//       const props = item.properties || {};
//       const rawTags = (props["_tags"] || "").toLowerCase();

//       if (rawTags.indexOf(BULK_TAG) !== -1) {
//         const measuraAmount = props["_measura_amount"];
//         if (measuraAmount) {
//           const parsed = parseFloat(measuraAmount);
//           if (!isNaN(parsed) && parsed > 0) {
//             total += parsed;
//           }
//         } else {
//           total += item.quantity;
//         }
//       }
//     });
//     return total;
//   }

//   // ─── CHECKOUT HANDLER ──────────────────────────────────────────────────────

//   /**
//    * Intercept the checkout button. Calculate fees, update the cart, then
//    * redirect to /checkout. Runs once per click — no loops, no reloads.
//    */
//   function handleCheckout(e) {
//     e.preventDefault();
//     e.stopPropagation();

//     // Disable the checkout button visually while processing
//     var btn = document.querySelector('[name="checkout"]') || e.currentTarget;
//     btn.disabled = true;
//     btn.style.opacity = "0.6";
//     btn.style.pointerEvents = "none";

//     console.log("[CartFees] Checkout clicked — calculating fees…");

//     fetchCart()
//       .then(function (cart) {
//         var items = cart.items || [];

//         // --- Calculate all fees ---
//         var totalPallets = calcTotalPallets(items);
//         var palletFeeQty = totalPallets;

//         var deliveryPallets = calcDeliveryPallets(items);
//         var deliveryFeeQty =
//           deliveryPallets > 0
//             ? Math.ceil(deliveryPallets / DELIVERY_PALLETS_PER_FEE)
//             : 0;

//         var bulkYards = calcBulkYards(items);
//         var dumpTruck1_6Qty = 0;
//         var dumpTruck7_10Qty = 0;

//         if (bulkYards > 0) {
//           var remaining = bulkYards;
//           while (remaining >= 7) {
//             dumpTruck7_10Qty += 1;
//             remaining -= 10;
//           }
//           if (remaining > 0) {
//             dumpTruck1_6Qty += 1;
//           }
//         }

//         console.log(
//           "[CartFees] Pallets:",
//           totalPallets,
//           "→ pallet fee:",
//           palletFeeQty,
//           "| Delivery pallets:",
//           deliveryPallets,
//           "→ delivery fee:",
//           deliveryFeeQty,
//           "| Bulk yards:",
//           bulkYards,
//           "→ dump 1-6:",
//           dumpTruck1_6Qty,
//           "dump 7-10:",
//           dumpTruck7_10Qty,
//         );

//         // --- Single update: set all fee quantities (0 = remove) ---
//         var updates = {};
//         updates[PALLET_FEE_VARIANT_ID] = palletFeeQty;
//         updates[DELIVERY_FEE_VARIANT_ID] = deliveryFeeQty;
//         updates[DUMP_TRUCK_1_6_VARIANT_ID] = dumpTruck1_6Qty;
//         updates[DUMP_TRUCK_7_10_VARIANT_ID] = dumpTruck7_10Qty;

//         return updateCartByKey(updates);
//       })
//       .then(function () {
//         console.log("[CartFees] Fees synced — redirecting to checkout");
//         window.location.href = "/checkout";
//       })
//       .catch(function (err) {
//         console.error("[CartFees] Error syncing fees at checkout:", err);
//         // Re-enable button so user can retry
//         btn.disabled = false;
//         btn.style.opacity = "";
//         btn.style.pointerEvents = "";
//         // Fall through to checkout anyway so the user isn't stuck
//         window.location.href = "/checkout";
//       });
//   }

//   // ─── HIDE FEE CONTROLS ON CART PAGE ─────────────────────────────────────────

//   /**
//    * On the cart page, hide the remove/trash buttons and quantity inputs for
//    * fee line items so users cannot manually delete or change them.
//    * Fees are fully managed by handleCheckout — recalculated on every click.
//    */
//   function lockFeeLineItems() {
//     // Build a set of fee variant ID strings for quick lookup
//     var feeIdSet = {};
//     ALL_FEE_VARIANT_IDS.forEach(function (id) {
//       feeIdSet[String(id)] = true;
//     });

//     // Strategy 1: Find elements with data-variant-id matching a fee
//     document.querySelectorAll("[data-variant-id]").forEach(function (el) {
//       if (feeIdSet[el.getAttribute("data-variant-id")]) {
//         hideFeeControls(el);
//       }
//     });

//     // Strategy 2: Find cart item links whose href contains the fee variant ID
//     ALL_FEE_VARIANT_IDS.forEach(function (id) {
//       document
//         .querySelectorAll('a[href*="' + id + '"]')
//         .forEach(function (link) {
//           var row = link.closest(
//             "tr, li, [class*='cart-item'], [class*='cart__item'], [class*='line-item']",
//           );
//           if (row) hideFeeControls(row);
//         });
//     });

//     // Strategy 3: Find remove buttons / quantity inputs whose name or value
//     // references a fee variant ID (Shopify uses updates[variant_id] pattern)
//     ALL_FEE_VARIANT_IDS.forEach(function (id) {
//       document
//         .querySelectorAll(
//           'input[name="updates[' +
//             id +
//             ']"], ' +
//             'a[href*="/cart/change?id=' +
//             id +
//             '"], ' +
//             '[data-line-item-key*="' +
//             id +
//             '"]',
//         )
//         .forEach(function (el) {
//           var row = el.closest(
//             "tr, li, [class*='cart-item'], [class*='cart__item'], [class*='line-item']",
//           );
//           if (row) hideFeeControls(row);
//         });
//     });
//   }

//   function hideFeeControls(row) {
//     // Hide remove / trash buttons
//     row
//       .querySelectorAll(
//         'a[href*="/cart/change"], [class*="remove"], [class*="delete"], ' +
//           '[class*="trash"], button[data-remove], [data-cart-remove], ' +
//           ".cart-item--remove, .cart__remove, .remove-item",
//       )
//       .forEach(function (btn) {
//         btn.style.display = "none";
//       });

//     // Hide quantity selectors (input + plus/minus buttons)
//     row
//       .querySelectorAll(
//         'input[type="number"], .quantity-selector, .quantity, ' +
//           '[class*="qty"], .cart-item--quantity, .cart__quantity-selector',
//       )
//       .forEach(function (el) {
//         el.style.pointerEvents = "none";
//         el.style.opacity = "0.5";
//         el.tabIndex = -1;
//         // For inputs, also make readonly
//         if (el.tagName === "INPUT") {
//           el.readOnly = true;
//         }
//       });

//     // Mark the row so we don't process it twice
//     row.setAttribute("data-fee-locked", "true");
//   }

//   // ─── BIND CHECKOUT BUTTON ──────────────────────────────────────────────────

//   function bindCheckoutButton() {
//     // The checkout button is type="submit" name="checkout" with form="cart-form",
//     // so we intercept the form's submit event to catch all submit paths
//     // (button click, Enter key, dynamic checkout buttons, etc.)
//     var form = document.getElementById("cart-form");
//     if (form && !form.hasAttribute("data-fees-bound")) {
//       form.addEventListener("submit", handleCheckout);
//       form.setAttribute("data-fees-bound", "true");
//       console.log("[CartFees] Bound checkout interceptor to #cart-form submit");
//     }

//     // Also catch direct checkout links/buttons outside the form
//     var buttons = document.querySelectorAll('a[href="/checkout"]');
//     buttons.forEach(function (btn) {
//       if (!btn.hasAttribute("data-fees-bound")) {
//         btn.addEventListener("click", handleCheckout);
//         btn.setAttribute("data-fees-bound", "true");
//       }
//     });
//   }

//   // ─── OBSERVE CART RE-RENDERS ────────────────────────────────────────────────

//   function observeCartForm() {
//     var target =
//       document.getElementById("cart-form") ||
//       document.querySelector('[action="/cart"]');
//     if (!target) return;

//     var section = target.closest(".shopify-section") || target.parentNode;
//     var debounceTimer;

//     var observer = new MutationObserver(function () {
//       clearTimeout(debounceTimer);
//       debounceTimer = setTimeout(function () {
//         console.log("[CartFees] Cart DOM changed — re-binding and re-locking");
//         bindCheckoutButton();
//         lockFeeLineItems();
//       }, 100);
//     });

//     observer.observe(section, { childList: true, subtree: true });
//   }

//   // ─── BOOT ──────────────────────────────────────────────────────────────────

//   function boot() {
//     bindCheckoutButton();
//     lockFeeLineItems();
//     observeCartForm();
//   }

//   if (document.readyState === "loading") {
//     document.addEventListener("DOMContentLoaded", boot);
//   } else {
//     boot();
//   }

//   // ─── FETCH INTERCEPTOR (for _tags injection only) ──────────────────────────

//   /**
//    * Intercept /cart/add calls to inject _tags and _pallet_qty from hidden
//    * form inputs. Measura builds its own payload and skips our form fields.
//    */
//   (function () {
//     function getHiddenFormProps() {
//       var props = {};
//       var tagsInput = document.querySelector('input[name="properties[_tags]"]');
//       var palletInput = document.querySelector(
//         'input[name="properties[_pallet_qty]"]',
//       );
//       if (tagsInput && tagsInput.value) {
//         props["_tags"] = tagsInput.value;
//       }
//       if (palletInput && palletInput.value) {
//         props["_pallet_qty"] = palletInput.value;
//       }
//       return props;
//     }

//     var originalFetch = window.fetch;
//     window.fetch = function (url, options) {
//       if (
//         typeof url === "string" &&
//         url.includes("/cart/add") &&
//         options &&
//         options.body
//       ) {
//         try {
//           var body =
//             typeof options.body === "string"
//               ? JSON.parse(options.body)
//               : options.body;
//           var formProps = getHiddenFormProps();
//           var modified = false;

//           if (body.items && Array.isArray(body.items)) {
//             body.items.forEach(function (item) {
//               if (ALL_FEE_VARIANT_IDS.indexOf(item.id) !== -1) return;
//               if (!item.properties) item.properties = {};
//               if (!item.properties["_tags"] && formProps["_tags"]) {
//                 item.properties["_tags"] = formProps["_tags"];
//                 modified = true;
//               }
//               if (!item.properties["_pallet_qty"] && formProps["_pallet_qty"]) {
//                 item.properties["_pallet_qty"] = formProps["_pallet_qty"];
//                 modified = true;
//               }
//             });
//           }

//           if (body.id && !body.items) {
//             if (ALL_FEE_VARIANT_IDS.indexOf(body.id) === -1) {
//               if (!body.properties) body.properties = {};
//               if (!body.properties["_tags"] && formProps["_tags"]) {
//                 body.properties["_tags"] = formProps["_tags"];
//                 modified = true;
//               }
//               if (!body.properties["_pallet_qty"] && formProps["_pallet_qty"]) {
//                 body.properties["_pallet_qty"] = formProps["_pallet_qty"];
//                 modified = true;
//               }
//             }
//           }

//           if (modified) {
//             console.log(
//               "[CartFees] Injected missing properties into /cart/add:",
//               formProps,
//             );
//             options = Object.assign({}, options, {
//               body: JSON.stringify(body),
//             });
//           }
//         } catch (e) {
//           // Body wasn't JSON — leave it alone
//         }
//       }

//       return originalFetch.call(this, url, options);
//     };
//   })();

//   // ─── EXPOSE FOR DEBUGGING ──────────────────────────────────────────────────

//   window.__cartFees = {
//     calcTotalPallets: calcTotalPallets,
//     calcDeliveryPallets: calcDeliveryPallets,
//     calcBulkYards: calcBulkYards,
//     handleCheckout: handleCheckout,
//   };
// })();
